/**
 * Feedback Card Component
 * The general per-message feedback surface: a collapsed badge (FeedbackBadge)
 * that expands into a card (FeedbackCard) composing whatever signals the
 * message carries — pronunciation scores (voice turns, every flow) and/or
 * AI analysis (grammar corrections, native expressions; analysis flows only).
 * Signals are independent and optional (pass at least one); future signals add
 * chips/sections here, not new surfaces.
 * Spec: docs/specs/mobile/conversation.mobile.md (CONV-015)
 */

import React, { useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import {
  Box,
  VStack,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import {
  ConversationAnalysis,
  PronunciationResult,
  GrammarError,
  LessonReference,
} from '../../types/conversation';
import { FS_XS, FS_SM } from '../../theme';
import { ChipButton } from '../ui';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { PronunciationBreakdown } from './PronunciationBreakdown';
import { getScoreStyleColor } from './pronunciationColors';

// Screen-specific constants
const FS_HEADER = 12;
const FS_LINE_HEIGHT_BODY = 18;
const BASE_ICON_BOX = 28;

export interface PracticeErrorParams {
  messageId: string;
  errorType: string;
  original: string;
  correction: string;
  explanation: string;
}

interface FeedbackCardProps {
  /** AI-analysis signal (grammar, native expression) — analysis flows only */
  analysis?: ConversationAnalysis;
  /** Pronunciation signal — voice messages, present in every flow */
  pronunciationResult?: PronunciationResult;
  isExpanded: boolean;
  messageId?: string;
  onReport?: (messageId: string) => void;
  onLessonPress?: (lesson: LessonReference) => void;
  onPracticePress?: (params: PracticeErrorParams) => void;
}

const GrammarErrorItem = React.memo(function GrammarErrorItem({
  error,
  messageId,
  onPracticePress,
}: {
  error: GrammarError;
  messageId?: string;
  onPracticePress?: (params: PracticeErrorParams) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const handlePractice = useCallback(() => {
    if (onPracticePress && messageId) {
      onPracticePress({
        messageId,
        errorType: error.type || 'other',
        original: error.original,
        correction: error.correction,
        explanation: error.explanation,
      });
    }
  }, [onPracticePress, messageId, error.type, error.original, error.correction, error.explanation]);

  const showPractice = onPracticePress && messageId;

  return (
    <Box
      style={{ backgroundColor: theme.errorBg }}
      p="$3"
      rounded="$lg"
      borderLeftWidth={3}
      borderLeftColor={theme.error}
    >
      <VStack space="sm">
        {/* Original with strikethrough */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.error, textDecorationLine: 'line-through', ...ffs }}
        >
          {error.original}
        </Text>

        {/* Correction with checkmark */}
        <HStack space="xs" alignItems="center">
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.success, fontWeight: '600', ...ffs }}>
            {'✓'} {error.correction}
          </Text>
        </HStack>

        {/* Explanation */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, lineHeight: Math.ceil(FS_LINE_HEIGHT_BODY * fontScale), color: theme.textSecondary, ...ffs }}
        >
          {error.explanation}
        </Text>

        {/* Practice this button */}
        {showPractice && (
          <ChipButton testID={`chat-practice-${error.type}`} label={t('chat.analysis.practiceThis')} onPress={handlePractice} variant="outline" softColor={theme.primarySoft} />
        )}
      </VStack>
    </Box>
  );
});

const PronunciationSection = React.memo(function PronunciationSection({ result }: { result: PronunciationResult }) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const score = Math.round(result.pronunciationScore);
  const iconBox = Math.round(BASE_ICON_BOX * fontScale);

  return (
    <VStack space="sm">
      {/* Section Header */}
      <HStack justifyContent="space-between" alignItems="center">
        <HStack space="xs" alignItems="center">
          <Box
            style={{ width: iconBox, height: iconBox, backgroundColor: theme.infoBg }}
            rounded="$lg"
            justifyContent="center"
            alignItems="center"
          >
            <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_SM * fontScale }}>🎤</Text>
          </Box>
          <Text
            allowFontScaling={false}
            accessibilityRole="header"
            style={{ fontSize: FS_HEADER * fontScale, color: theme.text, fontWeight: 'bold', textTransform: 'uppercase', ...ffs }}
          >
            {t('chat.analysis.pronunciationSection')}
          </Text>
        </HStack>
        <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.text, fontWeight: '600', ...ffs }}>
          {score}%
        </Text>
      </HStack>

      <PronunciationBreakdown result={result} />
    </VStack>
  );
});

const GrammarSection = React.memo(function GrammarSection({
  analysis,
  messageId,
  onPracticePress,
}: {
  analysis: ConversationAnalysis;
  messageId?: string;
  onPracticePress?: (params: PracticeErrorParams) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const hasErrors = analysis.grammarErrors.length > 0;
  const iconBox = Math.round(BASE_ICON_BOX * fontScale);

  if (!hasErrors) {
    return null;
  }

  return (
    <VStack space="sm">
      {/* Section Header */}
      <HStack justifyContent="space-between" alignItems="center">
        <HStack space="xs" alignItems="center">
          <Box
            style={{ width: iconBox, height: iconBox, backgroundColor: theme.errorBg }}
            rounded="$lg"
            justifyContent="center"
            alignItems="center"
          >
            <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_SM * fontScale }}>📝</Text>
          </Box>
          <Text
            testID="chat-analysis-header"
            allowFontScaling={false}
            accessibilityRole="header"
            style={{ fontSize: FS_HEADER * fontScale, color: theme.text, fontWeight: 'bold', textTransform: 'uppercase', ...ffs }}
          >
            {t('chat.analysis.grammarTipsSection')}
          </Text>
        </HStack>
        <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.error, fontWeight: 'bold', ...ffs }}>
          {analysis.grammarErrors.length}
        </Text>
      </HStack>

      {/* Grammar items */}
      <VStack space="sm">
        {analysis.grammarErrors.map((error, idx) => (
          <GrammarErrorItem
            key={idx}
            error={error}
            messageId={messageId}
            onPracticePress={onPracticePress}
          />
        ))}
      </VStack>
    </VStack>
  );
});

const NativeExpressionSection = React.memo(function NativeExpressionSection({ analysis }: { analysis: ConversationAnalysis }) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const iconBox = Math.round(BASE_ICON_BOX * fontScale);

  // Absent (omitted by the contract) as well as explicitly null.
  if (!analysis.nativeExpression) {
    return null;
  }

  return (
    <VStack space="sm">
      {/* Section Header */}
      <HStack space="xs" alignItems="center">
        <Box
          style={{ width: iconBox, height: iconBox, backgroundColor: theme.successBg }}
          rounded="$lg"
          justifyContent="center"
          alignItems="center"
        >
          <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_SM * fontScale }}>🗣️</Text>
        </Box>
        <Text
          allowFontScaling={false}
          accessibilityRole="header"
          style={{ fontSize: FS_HEADER * fontScale, color: theme.text, fontWeight: 'bold', textTransform: 'uppercase', ...ffs }}
        >
          {t('chat.analysis.soundMoreNativeSection')}
        </Text>
      </HStack>

      {/* Content box */}
      <Box style={{ backgroundColor: theme.successBg }} p="$3" rounded="$lg">
        <VStack space="xs">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_SM * fontScale, color: theme.success, fontWeight: '600', fontStyle: 'italic', ...ffs }}
          >
            "{analysis.nativeExpression.nativeVersion}"
          </Text>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, lineHeight: Math.ceil(FS_LINE_HEIGHT_BODY * fontScale), color: theme.textSecondary, ...ffs }}
          >
            {analysis.nativeExpression.explanation}
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
});

const LessonLinkItem = React.memo(function LessonLinkItem({
  lesson,
  onPress,
}: {
  lesson: LessonReference;
  onPress: (lesson: LessonReference) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const handlePress = useCallback(() => onPress(lesson), [onPress, lesson]);

  return (
    <TouchableOpacity
      testID={`chat-lesson-link-${lesson.slug}`}
      accessibilityLabel={`${t('chat.analysis.lessonLinkLabel')}${lesson.title}`}
      accessibilityRole="button"
      onPress={handlePress}
    >
      <Box
        style={{ backgroundColor: theme.primarySoft, borderColor: theme.primary }}
        borderWidth={1}
        p="$3"
        rounded="$lg"
      >
        <HStack justifyContent="space-between" alignItems="center">
          <VStack space="xs" flex={1}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
              numberOfLines={1}
            >
              {lesson.title}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {formatErrorType(lesson.errorType)}
            </Text>
          </VStack>
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
            {'›'}
          </Text>
        </HStack>
      </Box>
    </TouchableOpacity>
  );
});

const LessonLinksSection = React.memo(function LessonLinksSection({
  lessons,
  onLessonPress,
}: {
  lessons: LessonReference[];
  onLessonPress: (lesson: LessonReference) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const iconBox = Math.round(BASE_ICON_BOX * fontScale);

  if (lessons.length === 0) return null;

  return (
    <VStack space="sm">
      <HStack space="xs" alignItems="center">
        <Box
          style={{ width: iconBox, height: iconBox, backgroundColor: theme.primarySoft }}
          rounded="$lg"
          justifyContent="center"
          alignItems="center"
        >
          <Text allowFontScaling={false} accessible={false} importantForAccessibility="no" style={{ fontSize: FS_SM * fontScale }}>📚</Text>
        </Box>
        <Text
          allowFontScaling={false}
          accessibilityRole="header"
          style={{ fontSize: FS_HEADER * fontScale, color: theme.text, fontWeight: 'bold', textTransform: 'uppercase', ...ffs }}
        >
          {t('chat.analysis.studyThisSection')}
        </Text>
      </HStack>

      <VStack space="xs">
        {lessons.map((lesson) => (
          <LessonLinkItem key={lesson.lessonId} lesson={lesson} onPress={onLessonPress} />
        ))}
      </VStack>
    </VStack>
  );
});

function formatErrorType(errorType: string): string {
  return errorType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const FeedbackCard = React.memo(function FeedbackCard({
  analysis,
  pronunciationResult,
  isExpanded,
  messageId,
  onReport,
  onLessonPress,
  onPracticePress,
}: FeedbackCardProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  if (!isExpanded) {
    return null;
  }

  // The surface requires at least one signal
  if (!analysis && !pronunciationResult) {
    return null;
  }

  return (
    <Box
      style={{ backgroundColor: theme.card, borderColor: theme.border }}
      borderWidth={1}
      rounded="$lg"
      p="$4"
      mt="$2"
      hardShadow="2"
    >
      <VStack space="md">
        {/* Pronunciation signal (voice messages, every flow) */}
        {pronunciationResult && (
          <>
            <PronunciationSection result={pronunciationResult} />
            {analysis && <Box h={1} style={{ backgroundColor: theme.border }} />}
          </>
        )}

        {/* Analysis signal (grammar, lessons, native expression — analysis flows).
            The report affordance belongs to this signal: pronunciation scores are
            Azure-computed, not AI-generated. */}
        {analysis && (
          <>
            {/* Grammar Section */}
            {analysis.grammarErrors.length > 0 && (
              <>
                <GrammarSection analysis={analysis} messageId={messageId} onPracticePress={onPracticePress} />
                {(analysis.nativeExpression || (analysis.lessonReferences && analysis.lessonReferences.length > 0)) && (
                  <Box h={1} style={{ backgroundColor: theme.border }} />
                )}
              </>
            )}

            {/* Lesson Links Section */}
            {onLessonPress && analysis.lessonReferences && analysis.lessonReferences.length > 0 && (
              <>
                <LessonLinksSection lessons={analysis.lessonReferences} onLessonPress={onLessonPress} />
                {analysis.nativeExpression && (
                  <Box h={1} style={{ backgroundColor: theme.border }} />
                )}
              </>
            )}

            {/* Native Expression Section */}
            <NativeExpressionSection analysis={analysis} />

            {/* Encouragement bar */}
            {analysis.encouragement && (
              <Box
                style={{ backgroundColor: theme.primarySoft }}
                p="$3"
                rounded="$lg"
                alignItems="center"
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '600', textAlign: 'center', ...ffs }}
                >
                  {analysis.encouragement}
                </Text>
              </Box>
            )}

            {/* Footer row. Previously showed a per-message CEFR level badge,
                but `overallLevel` is never sent to clients — it stays
                server-side to feed the learner profile — so the badge only
                ever rendered from Storybook mocks. Spacer keeps the report
                button right-aligned. */}
            <HStack justifyContent="space-between" alignItems="center">
              <Box />

              {/* Report button */}
              {onReport && messageId && (
                <TouchableOpacity
                  testID="chat-button-report-analysis"
                  accessibilityLabel={t('chat.analysis.reportAnalysisLabel')}
                  accessibilityRole="button"
                  onPress={() => onReport(messageId)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
                    {t('chat.analysis.reportButton')}
                  </Text>
                </TouchableOpacity>
              )}
            </HStack>
          </>
        )}

        {/* Collapse hint */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.primary, textAlign: 'center', ...ffs }}
        >
          {t('chat.feedback.collapseHint')}
        </Text>
      </VStack>
    </Box>
  );
});

/**
 * Compact badge shown when the feedback surface is collapsed.
 * One chip per signal present; the "View" affordance always renders.
 */
export const FeedbackBadge = React.memo(function FeedbackBadge({
  analysis,
  pronunciationResult,
  onPress,
}: {
  analysis?: ConversationAnalysis;
  pronunciationResult?: PronunciationResult;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const grammarCount = analysis?.grammarErrors.length ?? 0;
  // pronunciationScore of 0 means assessment data unavailable (SDK workaround) — hide instead of showing 0%
  const pronScore = pronunciationResult && pronunciationResult.pronunciationScore > 0
    ? Math.round(pronunciationResult.pronunciationScore)
    : null;

  return (
    // testID keeps its historical value — specs/e2e chat selectors target it
    <TouchableOpacity testID="chat-button-analysis-badge" accessibilityLabel={t('chat.feedback.viewLabel')} accessibilityRole="button" onPress={onPress}>
      <Box
        style={{ backgroundColor: theme.card, borderColor: theme.border }}
        borderWidth={1}
        rounded="$full"
        px="$3"
        py="$1"
        alignSelf="flex-end"
        hardShadow="1"
      >
        <HStack space="xs" alignItems="center">
          {pronScore !== null && (
            <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: getScoreStyleColor(pronScore, theme), fontWeight: '600', ...ffs }}>
              🎤 {pronScore}%
            </Text>
          )}
          {grammarCount > 0 && (
            <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.text, fontWeight: '600', ...ffs }}>
              📝 {t('chat.analysis.tip', { count: grammarCount })}
            </Text>
          )}
          <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
            {t('chat.feedback.view')} ›
          </Text>
        </HStack>
      </Box>
    </TouchableOpacity>
  );
});
